import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { notificationService } from './clients/ApiClients';
import { NotificationResponse } from './clients/rust-api-client';
import LoadingSpinner from './LoadingSpinner';
import { Fact, Section } from './violation/linear/panelParts';
import {
  entityPath,
  isCommunication,
  notificationTypeLabel,
} from './notification/notificationMeta';
import { formatTimestamp } from './utils/dateUtils';
import { apiErrorMessage } from './utils/apiErrors';
import { ErrorPage } from './common/Custom/ErrorPage';
import { useSyncedNotification } from './sync/hooks';

const linkClass = 'text-accent-fg hover:underline';
const idClass = 'font-mono text-[12px]';

/**
 * One communication, read-only (#623): the message as the homeowner got
 * it, and every column of its row.
 */
const CommunicationDetailPage: React.FC = () => {
  const { communicationId } = useParams<{ communicationId: string }>();
  const [fetchedRow, setRow] = useState<NotificationResponse | null>(null);
  const [fetching, setLoading] = useState(true);
  // From the sync engine's copy when it holds this one (#561); a board
  // member's copy holds no residents' mail, so theirs is fetched.
  const synced = useSyncedNotification(communicationId);
  const haveSynced = !!synced;
  const row = synced ?? fetchedRow;
  const loading = !haveSynced && fetching;
  const [error, setError] = useState<string | null>(null);
  // Bumped by the error page's "Try again" to run the load again.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchRow = async (): Promise<void> => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await notificationService.getNotificationById({
          notificationId: communicationId!,
        });
        setRow(response.data);
      } catch (err) {
        console.error('Error fetching communication:', err);
        setError(apiErrorMessage(err, "Couldn't load this communication."));
      } finally {
        setLoading(false);
      }
    };

    if (communicationId) {
      fetchRow();
    }
  }, [communicationId, reloadKey, haveSynced]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!row) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="communications" />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this communication"
          message={
            error ??
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  const about = row ? entityPath(row) : null;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="communications" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to="/communications" className="text-fg-muted hover:text-fg">
          Communications
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        {row && (
          <span className="min-w-0 truncate font-medium text-fg">
            {row.title}
          </span>
        )}
      </div>

      {row && !isCommunication(row) && (
        <p className="mb-6 text-[13px] text-fg-secondary">
          This was not sent to a homeowner. It is internal staff mail, in{' '}
          <Link to={`/notifications/${row.id}`} className={linkClass}>
            Notifications
          </Link>
          .
        </p>
      )}

      {row && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-w-0">
            <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
              {row.title}
            </h1>
            <p className="mt-1 text-[13px] text-fg-muted">
              To {row.recipient_name}
              {row.recipient_email && ` <${row.recipient_email}>`} ·{' '}
              {formatTimestamp(row.created_at)}
            </p>
            <p className="mt-4 whitespace-pre-line text-[15px]/6 text-fg-secondary">
              {row.message}
            </p>
          </div>

          <aside
            aria-label="Communication properties"
            className="lg:sticky lg:top-6 lg:self-start"
          >
            <Section title="Recipient">
              <Fact label="Name">
                {row.member_id ? (
                  <Link
                    to={`/hoa/${row.hoa_id}/member/${row.member_id}`}
                    className={linkClass}
                  >
                    {row.recipient_name}
                  </Link>
                ) : (
                  row.recipient_name
                )}
              </Fact>
              <Fact label="Email">{row.recipient_email || '—'}</Fact>
              <Fact label="HOA">
                <Link to={`/hoa/${row.hoa_id}`} className={linkClass}>
                  {row.hoa_name}
                </Link>
              </Fact>
            </Section>
            <Section title="Delivery">
              <Fact label="Email">{row.email_sent ? 'Sent' : 'Not sent'}</Fact>
              <Fact label="Homeowner">{row.is_read ? 'Read' : 'Unread'}</Fact>
            </Section>
            <Section title="About">
              <Fact label="Type">
                {notificationTypeLabel(row.notification_type)}
              </Fact>
              <Fact label="Entity">
                {row.entity_id ? (
                  <>
                    {row.entity_type}{' '}
                    {about ? (
                      <Link to={about} className={`${linkClass} ${idClass}`}>
                        {row.entity_id}
                      </Link>
                    ) : (
                      <span className={idClass}>{row.entity_id}</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </Fact>
            </Section>
            <Section title="Record">
              <Fact label="ID">
                <span className={idClass}>{row.id}</span>
              </Fact>
              <Fact label="Member">
                <span className={idClass}>{row.member_id ?? '—'}</span>
              </Fact>
              <Fact label="User">
                <span className={idClass}>{row.user_id ?? '—'}</span>
              </Fact>
              <Fact label="HOA">
                <span className={idClass}>{row.hoa_id}</span>
              </Fact>
              <Fact label="Created">{formatTimestamp(row.created_at)}</Fact>
              <Fact label="Updated">{formatTimestamp(row.updated_at)}</Fact>
            </Section>
          </aside>
        </div>
      )}
    </SidebarLayout>
  );
};

export default CommunicationDetailPage;
